import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';
import { canAny } from '../hooks/usePermissions';
import { StarIcon, EyeSlashIcon, EyeIcon, FunnelIcon } from '@heroicons/react/24/outline';

const StarDisplay = ({ stars }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <StarIcon
        key={s}
        className={`w-4 h-4 ${s <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ))}
    <span className="ml-1 text-sm font-medium text-gray-700">{stars}</span>
  </span>
);

const Ratings = () => {
  const { admin } = useAuth();
  const canModerate = canAny(admin, ['manage_drivers', 'manage_users']);

  const [ratings, setRatings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  // filters
  const [rateeType, setRateeType] = useState('');
  const [stars, setStars] = useState('');
  const [moderationStatus, setModerationStatus] = useState('');
  const [sort, setSort] = useState('recent');
  const [showFilters, setShowFilters] = useState(false);

  // moderation modal
  const [modModal, setModModal] = useState(null); // { ratingId, currentStatus }
  const [modReason, setModReason] = useState('');
  const [modSubmitting, setModSubmitting] = useState(false);

  const fetchRatings = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 25, sort };
      if (rateeType) params.rateeType = rateeType;
      if (stars) params.stars = stars;
      if (moderationStatus) params.moderationStatus = moderationStatus;

      const res = await api.get('/admin/ratings', { params });
      setRatings(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  }, [rateeType, stars, moderationStatus, sort]);

  useEffect(() => { fetchRatings(1); }, [fetchRatings]);

  const openModModal = (rating) => {
    setModModal({ ratingId: rating._id, currentStatus: rating.moderation?.status || 'visible' });
    setModReason('');
  };

  const handleModerate = async () => {
    if (!modModal) return;
    const newStatus = modModal.currentStatus === 'visible' ? 'hidden' : 'visible';
    setModSubmitting(true);
    try {
      await api.patch(`/admin/ratings/${modModal.ratingId}/moderation`, {
        status: newStatus,
        reason: modReason || undefined,
      });
      toast.success(`Comment ${newStatus === 'hidden' ? 'hidden' : 'restored'}`);
      setModModal(null);
      fetchRatings(pagination.page);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Moderation failed');
    } finally {
      setModSubmitting(false);
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ratings & Reviews</h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} total ratings</p>
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <FunnelIcon className="w-4 h-4" />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="card p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ratee type</label>
            <select value={rateeType} onChange={(e) => setRateeType(e.target.value)} className="input-field w-full text-sm">
              <option value="">All</option>
              <option value="driver">Driver</option>
              <option value="user">User</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stars</label>
            <select value={stars} onChange={(e) => setStars(e.target.value)} className="input-field w-full text-sm">
              <option value="">All</option>
              <option value="1,2">Low (1–2★)</option>
              <option value="3">Neutral (3★)</option>
              <option value="4,5">High (4–5★)</option>
              {[1, 2, 3, 4, 5].map((s) => <option key={s} value={String(s)}>{s}★ only</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Moderation</label>
            <select value={moderationStatus} onChange={(e) => setModerationStatus(e.target.value)} className="input-field w-full text-sm">
              <option value="">All</option>
              <option value="visible">Visible</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Sort</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="input-field w-full text-sm">
              <option value="recent">Most recent</option>
              <option value="stars_asc">Stars low → high</option>
              <option value="stars_desc">Stars high → low</option>
            </select>
          </div>
        </div>
      )}

      {/* Needs Attention shortcut */}
      {!stars && !rateeType && (
        <button
          onClick={() => { setStars('1,2'); setRateeType('driver'); setShowFilters(true); }}
          className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-left"
        >
          <span className="text-red-500 font-semibold text-sm">⚠ Needs Attention</span>
          <span className="text-sm text-red-700">Click to see drivers with 1–2★ ratings</span>
        </button>
      )}

      <div className="card p-0">
        {loading ? (
          <LoadingSpinner className="py-16" />
        ) : ratings.length === 0 ? (
          <p className="text-center text-gray-500 py-12">No ratings found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="table-cell text-left">Stars</th>
                  <th className="table-cell text-left">Tags</th>
                  <th className="table-cell text-left">Comment</th>
                  <th className="table-cell text-left">Direction</th>
                  <th className="table-cell text-left">Ride</th>
                  <th className="table-cell text-left">Date</th>
                  <th className="table-cell text-left">Status</th>
                  {canModerate && <th className="table-cell text-left">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ratings.map((r) => {
                  const isHidden = r.moderation?.status === 'hidden';
                  return (
                    <tr key={r._id} className={`hover:bg-gray-50 ${isHidden ? 'opacity-60' : ''}`}>
                      <td className="table-cell"><StarDisplay stars={r.stars} /></td>
                      <td className="table-cell">
                        {r.tags && r.tags.length > 0
                          ? <div className="flex flex-wrap gap-1">{r.tags.map((t) => <span key={t} className="px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded-full">{t}</span>)}</div>
                          : <span className="text-gray-400 text-sm">—</span>}
                      </td>
                      <td className="table-cell max-w-xs">
                        {isHidden
                          ? <span className="text-xs text-gray-400 italic">[hidden]</span>
                          : <span className="text-sm text-gray-700 line-clamp-2">{r.comment || <span className="text-gray-400">—</span>}</span>}
                      </td>
                      <td className="table-cell">
                        <span className="text-xs text-gray-600 capitalize">{r.rater?.type} → {r.ratee?.type}</span>
                      </td>
                      <td className="table-cell">
                        {r.ride?._id
                          ? <Link to={`/rides/${r.ride._id}`} className="text-sm text-indigo-600 hover:underline font-mono">#{String(r.ride._id).slice(-6)}</Link>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="table-cell text-sm text-gray-600 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                      <td className="table-cell">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${isHidden ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {isHidden ? 'Hidden' : 'Visible'}
                        </span>
                      </td>
                      {canModerate && (
                        <td className="table-cell">
                          <button
                            onClick={() => openModModal(r)}
                            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                          >
                            {isHidden ? <><EyeIcon className="w-3.5 h-3.5" /> Show</> : <><EyeSlashIcon className="w-3.5 h-3.5" /> Hide</>}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <Pagination currentPage={pagination.page} totalPages={pagination.pages} onPageChange={(p) => fetchRatings(p)} />
      )}

      <Modal
        isOpen={!!modModal}
        onClose={() => setModModal(null)}
        title={modModal?.currentStatus === 'visible' ? 'Hide comment' : 'Restore comment'}
        footer={
          <div className="flex gap-3 justify-end">
            <button onClick={() => setModModal(null)} className="btn-secondary">Cancel</button>
            <button
              onClick={handleModerate}
              disabled={modSubmitting}
              className={modModal?.currentStatus === 'visible' ? 'btn-danger' : 'btn-primary'}
            >
              {modSubmitting ? 'Saving…' : modModal?.currentStatus === 'visible' ? 'Hide comment' : 'Restore comment'}
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-4">
          {modModal?.currentStatus === 'visible'
            ? 'The comment text will be hidden from app and admin views. The star score still counts toward the average.'
            : 'The comment will be made visible again.'}
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
          <textarea
            value={modReason}
            onChange={(e) => setModReason(e.target.value)}
            placeholder="e.g. Inappropriate language"
            className="input-field w-full h-20 resize-none"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Ratings;
